// The territory home's living backdrop (territory-ui.md §7): the client-side demo arena
// (demo.ts) stepped at its tick rate and drawn through the real ArenaStore and ArenaRenderer, the
// camera drifting after the leading bot at a wide spectator view. Used while the territory home
// is open and the client is not in an arena room (in one, the room itself is the backdrop).

import { ARENA_CLIENT, ARENA_EVENT } from '@ballio/shared';
import type { Ctx2D } from '../render/canvas.js';
import { ArenaCamera } from './camera.js';
import { DemoArena } from './demo.js';
import { ArenaEffects } from './effects.js';
import { ArenaRenderer } from './renderer.js';
import { ArenaStore } from './store.js';

/** Steps run at most per display frame (a long pause does not fast-forward minutes). */
const MAX_STEPS_PER_FRAME = 4;

export class ArenaBackdrop {
  readonly sim: DemoArena;
  readonly store: ArenaStore;
  private readonly camera: ArenaCamera;
  private readonly renderer: ArenaRenderer;
  private readonly effects = new ArenaEffects(0xbacd);
  private accMs = 0;
  private tick = 0;
  private follow = 0;
  private followUntilTick = 0;

  constructor(seed = 0x7e11) {
    this.sim = new DemoArena({ seed, bots: 10 });
    this.store = new ArenaStore(this.sim.info);
    this.camera = new ArenaCamera(this.sim.info);
    this.camera.mode = 'death'; // an eased pan toward the followed bot…
    this.camera.fixedZoom = ARENA_CLIENT.spectatorDiagonalScale; // …at the spectator view
    this.renderer = new ArenaRenderer(this.store);
    // Warm up so the first frame already shows grown territories.
    for (let i = 0; i < 25 * 40; i++) this.store.applyFrame(this.sim.step(), 0);
    this.store.flushDeferred();
    this.tick = this.store.lastTick;
    this.store.takeEvents();
  }

  /** Steps the sim by `dtMs` and draws one frame. */
  frame(
    ctx: Ctx2D,
    nowMs: number,
    dtMs: number,
    widthPx: number,
    heightPx: number,
    k: number,
  ): void {
    const tickMs = 1000 / this.sim.tickRate;
    this.accMs += Math.min(Math.max(dtMs, 0), tickMs * MAX_STEPS_PER_FRAME);
    while (this.accMs >= tickMs) {
      this.accMs -= tickMs;
      this.store.applyFrame(this.sim.step(), nowMs);
      this.tick = this.store.lastTick;
    }
    const rt = this.tick - 1 + this.accMs / tickMs;
    this.store.advance(rt);
    // Break-ups for the backdrop's deaths (no sounds, no labels).
    for (const q of this.store.takeEvents()) {
      if (q.event.type !== ARENA_EVENT.DEATH) continue;
      const snap = this.store.takeDeathSnapshot(q.event.victimId);
      if (!snap) continue;
      const trail: number[] = [];
      for (const c of snap.trail)
        trail.push((c % this.store.cols) + 0.5, Math.floor(c / this.store.cols) + 0.5);
      this.effects.burst(snap.edges, trail, snap.color, nowMs, () => true);
    }
    if (
      this.follow === 0 ||
      this.tick >= this.followUntilTick ||
      !this.store.heads.has(this.follow)
    ) {
      this.follow = this.sim.leaderId();
      this.followUntilTick = this.tick + 25 * 12;
    }
    const target = this.follow !== 0 ? this.store.sampleHead(this.follow, rt) : null;
    const view = this.camera.update(target, 0, dtMs, widthPx, heightPx);
    this.effects.update(nowMs, dtMs);
    this.renderer.draw(ctx, {
      view,
      k,
      nowMs,
      renderTick: rt,
      ownHead: null,
      formatBag: () => null,
      practice: false,
      crownId: 0,
      hideNames: false,
      effects: this.effects,
    });
  }
}
